export interface PurchaseCompletionOptions {
  onSuccess?: () => void;
  onCancel?: () => void;
  onError?: (error: { error: string }) => void;
}

export type PurchaseFailureCode = 'PAYMENT_ERROR' | 'PAYMENT_CANCELLED' |
  'PURCHASE_IN_PROGRESS' | 'NO_PRODUCTS' | 'PRODUCT_NOT_FOUND';

export interface PurchaseFailure {
  code: PurchaseFailureCode;
  message: string;
}

export interface PurchaseSuccess { status: 'success' }

/** Pure, dependency-free factory: also serialized into the iframe SDK like the
 * child-section resolver. Promise creation does not defer payment dispatch.
 * Existing callback-only consumers may ignore the returned Promise safely. */
export function createPurchaseCompletion(options: PurchaseCompletionOptions = {}) {
  let resolve!: (value: PurchaseSuccess) => void;
  let reject!: (reason: PurchaseFailure) => void;
  let settled = false;
  const promise = new Promise<PurchaseSuccess>((fulfill, fail) => { resolve = fulfill; reject = fail; });
  // Handle the original rejection for legacy callers, while returning the original
  // Promise so await/catch still observes its failure (never a fulfilled substitute).
  void promise.catch(() => {});
  function fail(error: { error: string }, code: PurchaseFailureCode = 'PAYMENT_ERROR') {
    if (settled) return;
    settled = true;
    reject({ code, message: error.error });
    if (typeof options.onError === 'function') options.onError(error);
  }
  return {
    promise,
    fail,
    onError: (error: { error: string }) => fail(error),
    onSuccess() {
      if (settled) return;
      settled = true;
      resolve({ status: 'success' });
      if (typeof options.onSuccess === 'function') options.onSuccess();
    },
    onCancel() {
      if (settled) return;
      settled = true;
      reject({ code: 'PAYMENT_CANCELLED', message: 'Payment canceled' });
      if (typeof options.onCancel === 'function') options.onCancel();
    },
  };
}
